import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    Signal,
    ViewChild,
    computed,
    inject,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UIRouterModule } from '@uirouter/angular';
import { StateService, TransitionService } from '@uirouter/core';
import { SessionService } from '@monsido/core/session/session.service';
import { DebugLogService } from '@monsido/core/services/debug-log/debug-log.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { LoginService } from '@client/app/core/services/login/login.service';
import { CoreAgreementService } from '@monsido/core/session/services/agreement/agreement.service';
import { ActiveRegionService } from '@client/app/blocks/service/active-region.service';
import { ApiResponseTypeOf } from '@client/types/helpers/api-helpers';
import { MenuItem } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { PopoverModule, Popover } from 'primeng/popover';
import { MenuModule, Menu } from 'primeng/menu';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { ProfileFoldoutComponent } from '@client/app/feature-modules/navigation/profile/profile-foldout/profile-foldout.component';
import { MyExportsDialogService } from '@client/app/shared-modules/my-exports/services/my-exports-dialog.service';
import { NotificationsPanelComponent } from '@client/app/feature-modules/navigation/notifications/components/notifications-panel/notifications-panel.component';
import { ActiveChatService } from '@client/app/modules/chat/services/active-chat.service';

const HELP_CENTER_FALLBACK_URL = 'https://docs.acquia.com/web-governance';
const TRAINING_REQUEST_FALLBACK_URL = 'https://calendly.com/d/4zz-3mp-vtb';
const TRAINING_REQUEST_US_FALLBACK_URL = 'https://calendly.com/monsido-us-cx';
const TRAINING_REQUEST_AU_FALLBACK_URL = 'https://calendly.com/monsido-au-cx';
const DOMAINS_OVERVIEW_STATE = 'base.customer.dashboard';

type ResellerSettings = NonNullable<
    NonNullable<NonNullable<ApiResponseTypeOf<'getMe', 200>['customer']>['reseller']>['settings']
>;

interface TopNavigationLink {
    label: Signal<string>;
    sref: string;
    isActive: Signal<boolean>;
    isVisible: Signal<boolean>;
}

interface TopNavigationUtilityItem {
    id: string;
    ariaLabel: Signal<string>;
    iconClass: string;
    isVisible: Signal<boolean>;
}

interface TopNavigationMobileUtilityItem extends TopNavigationUtilityItem {
    label: Signal<string>;
}

@Component({
    selector: 'mon-top-navigation',
    standalone: true,
    imports: [
        UIRouterModule,
        TranslocoModule,
        TooltipModule,
        NotificationsPanelComponent,
        PopoverModule,
        MenuModule,
        DialogModule,
        ButtonModule,
        ProfileFoldoutComponent,
    ],
    providers: [DialogService, MyExportsDialogService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './top-navigation.component.html',
    styleUrls: ['./top-navigation.component.css'],
})
export class TopNavigationComponent implements AfterViewInit {
    @ViewChild('topNavigationRoot') private readonly topNavigationRoot?: ElementRef<HTMLElement>;
    private readonly destroyRef = inject(DestroyRef);
    private readonly document = inject(DOCUMENT);
    private readonly transitionService = inject(TransitionService);
    private readonly stateService = inject(StateService);
    private readonly sessionService = inject(SessionService);
    private readonly debugLogService = inject(DebugLogService);
    private readonly activeFeatureService = inject(ActiveFeatureService);
    private readonly activeRegionService = inject(ActiveRegionService);
    private readonly loginService = inject(LoginService);
    private readonly agreementService = inject(CoreAgreementService);
    private readonly myExportsDialogService = inject(MyExportsDialogService);
    private readonly translocoService = inject(TranslocoService);
    private readonly activeChatService = inject(ActiveChatService);
    @ViewChild('helpMenu') private readonly helpMenu?: Menu;
    @ViewChild('profilePopover') private readonly profilePopover!: Popover;

    private readonly currentStateName = signal('');
    private readonly currentCustomerId = signal<number | null>(null);
    readonly isMobileMenuOpen = signal(false);
    readonly activeUtilityId = signal<string | null>(null);
    readonly logoutDialogVisible = signal(false);
    private readonly customer = toSignal(this.sessionService.customer$, {
        initialValue: this.sessionService.customer,
    });

    readonly currentUser = toSignal(this.sessionService.meObs, {
        initialValue: this.sessionService.me ?? null,
    });

    readonly hasMultipleAccounts = toSignal(this.agreementService.hasMultipleAgreements$, {
        initialValue: false,
    });

    readonly hasDataExport = computed(() => Boolean(this.customer()?.plan_traits?.data_export));
    readonly hasChatSupport = this.isFeatureActive('general_chat_support');
    readonly hasBookTraining = this.isFeatureActive('general_book_training');

    readonly hasCustomer = computed(() => {
        const customer = this.customer();
        const routeCustomerId = this.currentCustomerId();

        if (!customer || routeCustomerId === null) {
            return false;
        }

        return customer.id === routeCustomerId;
    });

    readonly isDomainsVisible = computed(() => this.hasCustomer());
    readonly isReportsVisible = computed(
        () => this.hasCustomer() && this.activeFeatureService.isFeatureActivePure('report_center_all'),
    );
    readonly isGlobalPoliciesVisible = computed(
        () => this.hasCustomer() && this.activeFeatureService.isFeatureActivePure('policies_all'),
    );
    readonly isHeatmapsVisible = computed(
        () => this.hasCustomer() && this.activeFeatureService.isFeatureActivePure('admin_heatmaps'),
    );
    readonly isAdminVisible = computed(
        () => this.hasCustomer() && this.activeFeatureService.isFeatureActivePure('customer_admin'),
    );
    readonly isNotificationsVisible = this.isFeatureActive('velaro');
    private readonly isAlwaysVisible = computed(() => true);

    readonly isDomainsActive = computed(() => {
        const stateName = this.currentStateName();
        return stateName === DOMAINS_OVERVIEW_STATE || stateName.startsWith('base.customer.domain');
    });
    readonly isReportsActive = computed(() => this.currentStateName().includes('base.customer.global.reportcenter'));
    readonly isGlobalPoliciesActive = computed(() => this.currentStateName().includes('base.customer.global.policy'));
    readonly isHeatmapsActive = computed(() => this.currentStateName().includes('base.customer.heatmaps'));
    readonly isAdminActive = computed(() => this.currentStateName().includes('base.customer.admin'));

    readonly topLinks: TopNavigationLink[] = [
        {
            label: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.link.domains'), { initialValue: '' }),
            sref: DOMAINS_OVERVIEW_STATE,
            isActive: this.isDomainsActive,
            isVisible: this.isDomainsVisible,
        },
        {
            label: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.link.reports'), { initialValue: '' }),
            sref: 'base.customer.global.reportcenter',
            isActive: this.isReportsActive,
            isVisible: this.isReportsVisible,
        },
        {
            label: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.link.globalPolicies'), { initialValue: '' }),
            sref: 'base.customer.global.policy.dashboard',
            isActive: this.isGlobalPoliciesActive,
            isVisible: this.isGlobalPoliciesVisible,
        },
        {
            label: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.link.heatmaps'), { initialValue: '' }),
            sref: 'base.customer.heatmaps.dashboard',
            isActive: this.isHeatmapsActive,
            isVisible: this.isHeatmapsVisible,
        },
        {
            label: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.link.admin'), { initialValue: '' }),
            sref: 'base.customer.admin.dashboard',
            isActive: this.isAdminActive,
            isVisible: this.isAdminVisible,
        },
    ];

    readonly utilityItems: TopNavigationUtilityItem[] = [
        {
            id: 'notifications',
            ariaLabel: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.utility.notifications'), { initialValue: '' }),
            iconClass: 'pi pi-bell',
            isVisible: this.isNotificationsVisible,
        },
        {
            id: 'help',
            ariaLabel: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.utility.help'), { initialValue: '' }),
            iconClass: 'pi pi-question-circle',
            isVisible: this.isAlwaysVisible,
        },
        {
            id: 'account',
            ariaLabel: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.utility.account'), { initialValue: '' }),
            iconClass: 'pi pi-user',
            isVisible: this.isAlwaysVisible,
        },
    ];

    readonly mobileUtilityItems: TopNavigationMobileUtilityItem[] = [
        {
            id: 'account',
            label: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.utility.account'), { initialValue: '' }),
            ariaLabel: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.utility.account'), { initialValue: '' }),
            iconClass: 'pi pi-user',
            isVisible: this.isAlwaysVisible,
        },
        {
            id: 'notifications',
            label: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.utility.notifications'), { initialValue: '' }),
            ariaLabel: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.utility.notifications'), { initialValue: '' }),
            iconClass: 'pi pi-bell',
            isVisible: this.isNotificationsVisible,
        },
        {
            id: 'help',
            label: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.utility.help'), { initialValue: '' }),
            ariaLabel: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.utility.help'), { initialValue: '' }),
            iconClass: 'pi pi-question-circle',
            isVisible: this.isAlwaysVisible,
        },
        {
            id: 'logout',
            label: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.utility.logout'), { initialValue: '' }),
            ariaLabel: toSignal(this.translocoService.selectTranslate('navigation.topNavigation.utility.logout'), { initialValue: '' }),
            iconClass: 'pi pi-sign-out',
            isVisible: this.isAlwaysVisible,
        },
    ];

    readonly visibleTopLinks = computed(() => this.topLinks.filter((link) => link.isVisible()));
    readonly visibleUtilityItems = computed(() => this.utilityItems.filter((item) => item.isVisible()));
    readonly visibleMobileUtilityItems = computed(() => this.mobileUtilityItems.filter((item) => item.isVisible()));
    readonly helpMenuItems = computed((): MenuItem[] => [
        {
            label: this.translocoService.translate('navigation.help.foldout.helpCenter'),
            state: { opensInNewTab: true },
            styleClass: 'top-nav__menu-item',
            command: () => this.onHelpCenter(),
        },
        ...(this.hasChatSupport() ? [{
            label: this.translocoService.translate('navigation.help.foldout.contactSupport'),
            styleClass: 'top-nav__menu-item',
            command: () => this.onContactSupport(),
        }] : []),
        ...(this.hasBookTraining() ? [{
            label: this.translocoService.translate('navigation.help.foldout.scheduleTraining'),
            state: { opensInNewTab: true },
            styleClass: 'top-nav__menu-item',
            command: () => this.onScheduleTraining(),
        }] : []),
        {
            label: this.translocoService.translate('navigation.help.foldout.exportSupportLog'),
            styleClass: 'top-nav__menu-item',
            command: () => this.onExportSupportLog(),
        },
    ]);

    constructor () {
        this.setupViewportTracking();
        this.setupRouteTracking();
    }

    ngAfterViewInit (): void {
        this.scheduleTopNavigationOffsetSync();
    }

    toggleMobileMenu (): void {
        this.isMobileMenuOpen.update((isMobileMenuOpen) => !isMobileMenuOpen);
        this.scheduleTopNavigationOffsetSync();
    }

    closeMobileMenu (): void {
        this.isMobileMenuOpen.set(false);
        this.scheduleTopNavigationOffsetSync();
    }

    closeActivePanel (): void {
        this.helpMenu?.hide();
        this.profilePopover?.hide();
    }

    onUtilityButtonClick (utilityId: string, event: MouseEvent): void {
        if (utilityId === 'account') {
            this.profilePopover.toggle(event);
            return;
        }

        if (utilityId === 'help') {
            this.helpMenu?.toggle(event);
            return;
        }

        this.activeUtilityId.update((current) => current === utilityId ? null : utilityId);
    }

    onMobileUtilityClick (utilityId: string): void {
        this.activeUtilityId.set(utilityId);
        this.closeMobileMenu();

        if (utilityId === 'notifications') {
            this.stateService.go('base.customer.notifications');
            return;
        }

        if (utilityId === 'account') {
            this.stateService.go('base.customer.edit-profile');
            return;
        }

        if (utilityId === 'help') {
            this.openHelpCenter();
            return;
        }

        if (utilityId === 'logout') {
            this.openLogoutDialog();
        }
    }

    onProfileEditProfile (): void {
        this.closeActivePanel();
        this.stateService.go('base.customer.edit-profile');
    }

    onProfileMyExports (): void {
        this.closeActivePanel();
        this.myExportsDialogService.open();
    }

    onProfileSwitchAccount (): void {
        this.closeActivePanel();
        this.stateService.go('base.dashboard');
    }

    onBrandClick (): void {
        this.closeMobileMenu();
        this.stateService.go(DOMAINS_OVERVIEW_STATE);
    }

    onProfileLogout (): void {
        this.closeActivePanel();
        this.openLogoutDialog();
    }

    onHelpCenter (): void {
        this.openHelpCenter();
    }

    onContactSupport (): void {
        this.activeChatService.openChat();
    }

    onScheduleTraining (): void {
        this.openInNewTab(this.resolveTrainingRequestUrl());
    }

    onExportSupportLog (): void {
        this.exportSupportLogs();
    }

    onUtilityOverlayHide (utilityId: string): void {
        if (this.activeUtilityId() === utilityId) {
            this.activeUtilityId.set(null);
        }
    }

    onLogoutDialogReject (): void {
        this.logoutDialogVisible.set(false);
    }

    onLogoutDialogAccept (): void {
        this.logoutDialogVisible.set(false);
        this.loginService.logout();
    }

    private openLogoutDialog (): void {
        this.logoutDialogVisible.set(true);
    }

    private openHelpCenter (): void {
        this.openInNewTab(this.resolveHelpCenterUrl());
    }

    private openInNewTab (url: string): void {
        const windowRef = this.document.defaultView;

        if (!windowRef) {
            return;
        }

        windowRef.open(url, '_blank', 'noopener,noreferrer');
    }

    private resolveHelpCenterUrl (): string {
        const helpCenterLink = this.getHelpCenterLink(this.sessionService.me);

        if (this.isValidHelpCenterLink(helpCenterLink)) {
            return helpCenterLink;
        }

        return HELP_CENTER_FALLBACK_URL;
    }

    private resolveTrainingRequestUrl (): string {
        const trainingRequestLink = this.getTrainingRequestLink(this.sessionService.me);

        if (this.isValidExternalUrl(trainingRequestLink)) {
            return trainingRequestLink;
        }

        if (this.activeRegionService.isInRegion('US')) {
            return TRAINING_REQUEST_US_FALLBACK_URL;
        }

        if (this.activeRegionService.isInRegion('AU')) {
            return TRAINING_REQUEST_AU_FALLBACK_URL;
        }

        return TRAINING_REQUEST_FALLBACK_URL;
    }

    private exportSupportLogs (): void {
        const windowRef = this.document.defaultView;

        if (!windowRef) {
            return;
        }

        const userId = this.currentUser()?.id ?? 'unknown-user';
        const logsString = JSON.stringify(this.debugLogService.logs || [], null, 2);
        const blob = new windowRef.Blob([logsString], { type: 'application/json' });
        const url = windowRef.URL.createObjectURL(blob);
        const anchor = this.document.createElement('a');

        anchor.setAttribute('href', url);
        anchor.setAttribute('download', `logs-${userId}.json`);
        anchor.click();
        windowRef.URL.revokeObjectURL(url);
    }

    private isValidHelpCenterLink (
        helpCenterLink: ResellerSettings['help_center_link'],
    ): helpCenterLink is string {
        return this.isValidExternalUrl(helpCenterLink);
    }

    private isValidExternalUrl (url: unknown): url is string {
        return typeof url === 'string' && /^https?:\/\//.test(url);
    }

    private getHelpCenterLink (profile: unknown): ResellerSettings['help_center_link'] {
        const settings = this.getResellerSettings(profile);

        if (!settings) {
            return undefined;
        }

        const helpCenterLink = settings['help_center_link'];
        return typeof helpCenterLink === 'string' ? helpCenterLink : undefined;
    }

    private getTrainingRequestLink (profile: unknown): ResellerSettings['training_request_link'] {
        const settings = this.getResellerSettings(profile);

        if (!settings) {
            return undefined;
        }

        const trainingRequestLink = settings['training_request_link'];
        return typeof trainingRequestLink === 'string' ? trainingRequestLink : undefined;
    }

    private getResellerSettings (profile: unknown): Record<string, unknown> | undefined {
        if (!this.isRecord(profile)) {
            return undefined;
        }

        const customer = profile['customer'];
        if (!this.isRecord(customer)) {
            return undefined;
        }

        const reseller = customer['reseller'];
        if (!this.isRecord(reseller)) {
            return undefined;
        }

        const settings = reseller['settings'];
        if (!this.isRecord(settings)) {
            return undefined;
        }

        return settings;
    }

    private isRecord (value: unknown): value is Record<string, unknown> {
        return value !== null && typeof value === 'object';
    }

    private isFeatureActive (feature: string): Signal<boolean> {
        return computed(() => {
            this.customer();
            return this.activeFeatureService.isFeatureActivePure(feature);
        });
    }

    private setupRouteTracking (): void {
        this.currentStateName.set(this.stateService.current.name || '');
        this.currentCustomerId.set(this.parseCustomerId(this.stateService.params?.customerId));

        const deregisterHook = this.transitionService.onSuccess({}, (transition) => {
            this.currentStateName.set(transition.to().name || '');
            this.currentCustomerId.set(this.parseCustomerId(transition.params().customerId));
            this.closeMobileMenu();
        }) as unknown as () => void;

        this.destroyRef.onDestroy(() => {
            deregisterHook();
        });
    }

    private setupViewportTracking (): void {
        const windowRef = this.document.defaultView;

        if (!windowRef) {
            return;
        }

        const closeMobileMenuOnDesktopViewport = (): void => {
            if (windowRef.innerWidth > 768) {
                this.closeMobileMenu();
            }

            this.scheduleTopNavigationOffsetSync();
        };

        const onWindowKeydown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && this.isMobileMenuOpen()) {
                this.closeMobileMenu();
            }
        };

        windowRef.addEventListener('resize', closeMobileMenuOnDesktopViewport, { passive: true });
        windowRef.addEventListener('keydown', onWindowKeydown);

        this.destroyRef.onDestroy(() => {
            windowRef.removeEventListener('resize', closeMobileMenuOnDesktopViewport);
            windowRef.removeEventListener('keydown', onWindowKeydown);
        });
    }

    private scheduleTopNavigationOffsetSync (): void {
        const windowRef = this.document.defaultView;
        if (windowRef && typeof windowRef.requestAnimationFrame === 'function') {
            windowRef.requestAnimationFrame(() => this.syncTopNavigationOffset());
            return;
        }

        this.syncTopNavigationOffset();
    }

    private syncTopNavigationOffset (): void {
        const topNavigationElement = this.topNavigationRoot?.nativeElement;
        if (!topNavigationElement) {
            return;
        }

        const renderedHeight = Math.ceil(topNavigationElement.getBoundingClientRect().height);
        if (renderedHeight <= 0) {
            return;
        }

        this.document.documentElement.style.setProperty('--gel-left-nav-offset-top', `${renderedHeight}px`);
    }

    private parseCustomerId (customerId: unknown): number | null {
        if (customerId === null || customerId === undefined) {
            return null;
        }

        const parsedCustomerId = Number.parseInt(String(customerId), 10);
        return Number.isNaN(parsedCustomerId) ? null : parsedCustomerId;
    }
}
